// LLM 流式输出偶发丢失表格行之间的换行（prod 实测 2026-09-09 ニュウマン高輪 bot），形如：
//   | 階 | 店舗名 | 商品 |
//   |---|---|---|| South・1F | A | x || South・2F | B | y |
//   | North・1F | C | z |
// 分隔行与首行、首行与次行被粘在同一行，GFM 解析器不认为是表格，整段按裸文本渲染。
// 这里只做渲染前的兜底修复，不改消息原文（TTS / 复制 / 落库不受影响）：
// 1. 分隔行（|---|---|）后紧跟 `|` 的，补一个换行
// 2. 表体行里以 `||` 粘连、且拆开后每段的单元格数都等于表头列数的，按 `||` 拆成多行
//    （单元格数不匹配时不动，避免误伤合法的空单元格 `||`）

/** 匹配代码围栏（``` / ~~~），围栏内部原样保留 */
const CODE_FENCE_RE = /(```[\s\S]*?(?:```|$)|~~~[\s\S]*?(?:~~~|$))/g;

/** 表格分隔行：|---|:--:|---| 形态 */
const SEPARATOR_LINE_RE = /^\s*\|?(?:\s*:?-+:?\s*\|)+\s*:?-*:?\s*$/;

/** 分隔行后没有换行直接接下一行（`|---|---|` 紧跟 `|`） */
const GLUED_SEPARATOR_RE = /^([ \t]*\|?(?:[ \t]*:?-+:?[ \t]*\|)+)(?=[ \t]*\|[^\n])/gm;

const isTableLine = (line: string) => line.includes('|');

/** 统计一行的单元格数（去掉首尾的 `|` 后按 `|` 切分，`\|` 转义不算分隔） */
const countCells = (line: string): number => {
  // 不用 lookbehind（旧运行时支持不一致），先剔除转义竖线再切分
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '').replace(/\\\|/g, '');
  return trimmed.split('|').length;
};

/** 把 `||` 粘连的一行拆成多行；拆开后任一段列数不等于 columns 则返回 null */
const splitGluedRow = (line: string, columns: number): string[] | null => {
  if (!line.includes('||')) return null;
  const segments = line.split('||');
  if (segments.length < 2) return null;

  const rows = segments.map((segment, index) => {
    const isFirst = index === 0;
    const isLast = index === segments.length - 1;
    const withLeading = isFirst ? segment : `|${segment}`;
    return isLast ? withLeading : `${withLeading}|`;
  });

  return rows.every((row) => countCells(row) === columns) ? rows : null;
};

const repairSegment = (segment: string): string => {
  const withSeparatorBreaks = segment.replace(GLUED_SEPARATOR_RE, '$1\n');
  const lines = withSeparatorBreaks.split('\n');
  const output: string[] = [];
  let columns = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const next = lines[i + 1];

    // 表头 + 分隔行：进入表格状态并记录列数
    if (columns === 0 && isTableLine(line) && next !== undefined && SEPARATOR_LINE_RE.test(next)) {
      columns = countCells(line);
      output.push(line);
      continue;
    }

    if (columns > 0) {
      if (SEPARATOR_LINE_RE.test(line)) {
        output.push(line);
        continue;
      }
      if (!line.trim() || !isTableLine(line)) {
        // 空行或非表格行：表格结束
        columns = 0;
        output.push(line);
        continue;
      }
      if (countCells(line) > columns) {
        const rows = splitGluedRow(line, columns);
        if (rows) {
          output.push(...rows);
          continue;
        }
      }
    }

    output.push(line);
  }

  return output.join('\n');
};

/**
 * 渲染前调用：修复 LLM 输出中表格行之间丢失的换行。
 * 不含表格特征（`|` + `---`）的文本直接原样返回，零开销。
 */
export const repairMarkdownTableRows = (content: string): string => {
  if (!content || !content.includes('|') || !content.includes('-')) return content;
  return content
    .split(CODE_FENCE_RE)
    .map((segment, index) => (index % 2 === 1 ? segment : repairSegment(segment)))
    .join('');
};
